/**
 * Value noise in 16.16 fixed point. Deterministic and integer-only, so terrain is identical
 * on every platform. See spec/core-spec.md, section "Value noise".
 */
import { floorDiv, mix32 } from "./hash.ts";

/** One in Q16 fixed point. */
export const Q16_ONE = 65536;

/** Smoothstep t*t*(3 - 2t) for t in [0, 65536], result in [0, 65536]. */
export function smoothstep16(t: number): number {
  // t*t < 2^32 and (3*ONE - 2t) < 2^18, so the product is below 2^50 and exact.
  return Math.floor((t * t * (3 * Q16_ONE - 2 * t)) / 4294967296);
}

/** Linear interpolation a + (b - a) * t with t in Q16, rounding toward negative infinity. */
export function lerp16(a: number, b: number, t: number): number {
  return a + Math.floor(((b - a) * t) / Q16_ONE);
}

/**
 * Value noise at integer tile coordinates (x, y) with an integer lattice period and a salt.
 * Returns a value in [0, 65535]. Corner values are the top 16 bits of mix32 of the lattice
 * point and the salt; interpolation is bilinear with smoothstep weights.
 */
export function valueNoise16(x: number, y: number, period: number, salt: number): number {
  const xi = floorDiv(x, period);
  const yi = floorDiv(y, period);
  const xf = x - xi * period;
  const yf = y - yi * period;
  const u = smoothstep16(Math.floor((xf * Q16_ONE) / period));
  const v = smoothstep16(Math.floor((yf * Q16_ONE) / period));
  const a = mix32(xi, yi, salt) >>> 16;
  const b = mix32(xi + 1, yi, salt) >>> 16;
  const c = mix32(xi, yi + 1, salt) >>> 16;
  const d = mix32(xi + 1, yi + 1, salt) >>> 16;
  return lerp16(lerp16(a, b, u), lerp16(c, d, u), v);
}
